import React, { useContext, useState, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { getStorageKey, removeStorageKey } from "../../core/utils/storage";
import { DataHelper } from "../../helpers";
import { Window, SettingsContext } from "../../contexts";

import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";

const configs = {
  1: {
    component: Step1,
  },
  2: {
    component: Step2,
  },
  3: {
    component: Step3,
  },
};

const CreateAccountComp = ({
  step,
  inLanding,
  setStep,
  onAccountCreated,
  onCancel,
  email = "",
  allowBack,
  showSocialBtns = true,
  isMerchantClient = false,
  isRetailCircle = false,
}) => {
  const { isShopify, isMerchant } = useContext(SettingsContext);
  const location = useLocation();
  const invitedUser = Window.INVITED_USER;

  const getShopifyDefaults = () => {
    if (!isShopify || isMerchant) {
      return {};
    }

    const params = location.search
      .slice(1)
      .split("&")
      .map((x) => x.split("="))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    return [
      "email",
      "first_name",
      "last_name",
      "phone",
      "store_link",
      "store_name",
      "birth_date",
    ].reduce((acc, key) => {
      if (params[key]) {
        try {
          acc[key] = decodeURIComponent(params[key]);
        } catch (e) {
          // Ignore
        }
      }

      return acc;
    }, {});
  };

  const defaultValues = {
    email,
    ...invitedUser,
    expert_categories: invitedUser?.expert_categories?.map(
      (category) => category.id
    ),
    interest_categories: invitedUser?.interest_categories?.map(
      (category) => category.id
    ),
    ...getShopifyDefaults(),
  };

  const [values, setValues] = useState(defaultValues);

  if (getStorageKey("referralType") === "member") {
    setStep(1, true);
  }

  if (step > 1 && !values.email) {
    setStep(1, true);
  }

  const goBack = () => {
    if (step < 2) {
      return;
    }

    setStep(step - 1);
  };

  const addToValues = (newValues) => {
    const _newValues = {
      ...values,
      ...newValues,
    };
    setValues(_newValues);
    return _newValues;
  };

  const onStepCompleted = function(step, newValues) {
    if (document.getElementById("secret-code").value) {
      // This field should be empty. If it got here, it must be a bot.
      return;
    }

    addToValues(newValues);
    setStep(step + 1);
  };
  const recaptchaRef = useRef();

  const onFinalStepCompleted = async function(step, newValues) {
    const token = await recaptchaRef.current.executeAsync("create-account");

    const referralCode = getStorageKey("referralCode");
    const productReferralCode = getStorageKey("productReferralCode");

    if (referralCode) {
      newValues["referral_code"] = referralCode;
    }

    if (newValues?.store_address?.locality) {
      newValues.store_address.city = newValues.store_address.locality;
      delete newValues.store_address.locality;
    }

    const data = addToValues(newValues);

    if (data?.store_address?.locality) {
      data.store_address.city = data.store_address.locality;
      delete data.store_address.locality;
    }

    const payload = {
      ...data,
      store_address: {},
      title:
        data.title == DataHelper.MANUAL_WORK_TITLE
          ? data.manual_title
          : data.title,
      product_referral_code: productReferralCode,
      type: isMerchantClient ? "merchant" : "client",
      recaptcha: token,
    };

    // RC: don't send password (OTP-based auth)
    if (isRetailCircle) {
      delete payload.password;
      delete payload.password2;
    }

    const response = await axios.post(
      isMerchantClient ? "/api/users/create-merchant-client" : "/api/users",
      payload
    );

    removeStorageKey("referralCode");

    // RC: pass phone number to Step3 for OTP verification
    onAccountCreated(response.data, isRetailCircle ? data.phone : null);
  };

  const config = configs[step];
  const BodyComponent = config.component;

  return (
    <BodyComponent
      onStepCompleted={(values) => onStepCompleted(step, values)}
      onFinalStepCompleted={(values) => onFinalStepCompleted(step, values)}
      onCancel={onCancel}
      values={values}
      invitedUser={invitedUser}
      goBack={allowBack && goBack}
      showSocialBtns={showSocialBtns}
      isMerchantClient={isMerchantClient}
      isRetailCircle={isRetailCircle}
      recaptchaRef={recaptchaRef}
    />
  );
};

export default CreateAccountComp;
